import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, FlaskConical, CheckCircle, AlertTriangle } from 'lucide-react';

/**
 * Pre-loads a preview of which patients are eligible, then on confirm
 * generates Draft test claims (one per eligible patient) for ODM CERT.
 *
 * Service dates are spread across weekdays in the last 14 days, units
 * vary 1-5 hours, and each patient's primary ICD-10 + a random billing
 * line drive payer_type / payer_program / procedure_code. The new claims
 * land as Draft so the user can review on /claims, then use the existing
 * 'Send N to ODM' flow to ship the batch.
 */
export const GenerateTestClaimsDialog = ({ open, onClose, onGenerated }) => {
  const [limit, setLimit] = useState(50);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setGenerating(false);
      return;
    }
    const run = async () => {
      setLoadingPreview(true);
      try {
        const res = await api.post('/claims/generate-test-batch/preview', { limit });
        setPreview(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load preview');
        onClose();
      } finally {
        setLoadingPreview(false);
      }
    };
    run();
  }, [open, limit, onClose]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/claims/generate-test-batch', { limit });
      const { total_created: created, total_skipped: skipped } = res.data;
      if (created > 0) {
        toast.success(`Created ${created} Draft test claim${created === 1 ? '' : 's'}`);
      }
      if (skipped > 0) {
        toast.warning(`Skipped ${skipped} patient${skipped === 1 ? '' : 's'} (missing data)`);
      }
      onGenerated?.(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate test claims');
    } finally {
      setGenerating(false);
    }
  };

  const eligible = preview?.eligible || [];
  const skipped = preview?.skipped || [];
  const claimsToCreate = preview?.claims_to_create ?? 0;
  const eligiblePatientCount = preview?.total_eligible_patients ?? 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="gen-test-claims-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5 text-amber-600" />
            Generate Test Claims for ODM CERT
          </DialogTitle>
          <DialogDescription>
            Builds <strong>{limit} Draft claims</strong> for the ODM CERT test file. Per ODM&apos;s
            guidance: a single batch is one payer (this generator uses <strong>ODM Fee-for-Service</strong> only),
            50 claims total &mdash; not 50 patients &mdash; so each eligible patient gets multiple claims
            with different service dates spread across the past 14 weekdays.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3 py-2">
          <label htmlFor="gen-limit" className="text-sm font-medium text-slate-700">
            How many claims?
          </label>
          <Input
            id="gen-limit"
            type="number"
            min={1}
            max={500}
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value, 10) || 1)}
            className="w-24"
            data-testid="gen-test-claims-limit"
          />
          <span className="text-xs text-slate-500">(up to 500)</span>
        </div>

        {loadingPreview ? (
          <div className="py-12 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            <span className="text-sm">Checking your patients…</span>
          </div>
        ) : preview && (
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-md border border-emerald-200 bg-emerald-50/60 px-4 py-3">
                <div className="text-2xl font-bold text-emerald-700" data-testid="gen-test-claims-claim-count">
                  {claimsToCreate}
                </div>
                <div className="text-xs uppercase tracking-wide text-emerald-700/80 font-semibold mt-0.5">
                  Claims to create
                </div>
              </div>
              <div className="rounded-md border border-indigo-200 bg-indigo-50/60 px-4 py-3">
                <div className="text-2xl font-bold text-indigo-700" data-testid="gen-test-claims-eligible">
                  {eligiblePatientCount}
                </div>
                <div className="text-xs uppercase tracking-wide text-indigo-700/80 font-semibold mt-0.5">
                  Eligible patients
                </div>
              </div>
              <div className="rounded-md border border-amber-200 bg-amber-50/60 px-4 py-3">
                <div className="text-2xl font-bold text-amber-700" data-testid="gen-test-claims-skipped">
                  {preview.total_skipped}
                </div>
                <div className="text-xs uppercase tracking-wide text-amber-700/80 font-semibold mt-0.5">
                  Skipped (missing data)
                </div>
              </div>
            </div>

            {eligiblePatientCount > 0 && claimsToCreate > eligiblePatientCount && (
              <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700">
                <span className="font-semibold">Round-robin:</span> each of the {eligiblePatientCount}{' '}
                eligible patient{eligiblePatientCount === 1 ? '' : 's'} will receive ~
                <span className="font-mono">{Math.ceil(claimsToCreate / eligiblePatientCount)}</span>{' '}
                claim{Math.ceil(claimsToCreate / eligiblePatientCount) === 1 ? '' : 's'} with different
                service dates so the file hits exactly {claimsToCreate} claims as ODM requires.
              </div>
            )}

            {eligible.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5 flex items-center gap-1">
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                  Eligible patients ({eligible.length})
                </div>
                <div className="border border-slate-200 rounded-md max-h-40 overflow-y-auto divide-y divide-slate-100">
                  {eligible.slice(0, 25).map((p) => (
                    <div key={p.patient_id} className="px-3 py-1.5 flex items-center justify-between text-xs">
                      <span className="text-slate-800">{p.patient_name}</span>
                      <span className="font-mono text-slate-500">{p.medicaid_id_masked}</span>
                    </div>
                  ))}
                  {eligible.length > 25 && (
                    <div className="px-3 py-1.5 text-xs text-slate-500 italic">
                      …and {eligible.length - 25} more
                    </div>
                  )}
                </div>
              </div>
            )}

            {skipped.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5 flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                  Will skip ({skipped.length}) — fix the data and re-run
                </div>
                <div className="border border-slate-200 rounded-md max-h-32 overflow-y-auto divide-y divide-slate-100">
                  {skipped.slice(0, 15).map((p) => (
                    <div key={p.patient_id} className="px-3 py-1.5 text-xs">
                      <span className="text-slate-800 font-medium">{p.patient_name}</span>
                      <span className="text-amber-700 ml-2">— {p.reason}</span>
                    </div>
                  ))}
                  {skipped.length > 15 && (
                    <div className="px-3 py-1.5 text-xs text-slate-500 italic">
                      …and {skipped.length - 15} more
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={generating} data-testid="gen-test-claims-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={generating || loadingPreview || !eligiblePatientCount}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="gen-test-claims-confirm"
          >
            {generating ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating…</>
            ) : (
              <><FlaskConical className="h-4 w-4 mr-2" />Generate {claimsToCreate} test claim{claimsToCreate === 1 ? '' : 's'}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateTestClaimsDialog;
